import { AlertCircle, ArrowLeft, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowBuilder } from "@/components/flows/FlowBuilder";
import { useToast } from "@/components/shared";
import { flowService } from "@/lib/services";
import type { Flow } from "@/types";

export function FlowEdit() {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<Flow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [flowName, setFlowName] = useState("");
  const [flowDescription, setFlowDescription] = useState("");
  const [showDetailsEdit, setShowDetailsEdit] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (flowId) {
      loadFlow();
    }
  }, [flowId]);

  const loadFlow = async () => {
    if (!flowId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await flowService.get(flowId);
      setFlow(response.data);
      setFlowName(response.data.name);
      setFlowDescription(response.data.description || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load flow");
      console.error("Error loading flow:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFlow = async (
    steps: Array<{
      agent_uuid?: string;
      step_type?: string;
      instructions?: string;
      expected_output?: string;
      config?: Record<string, unknown>;
      requires_review?: boolean;
    }>,
    connectors?: Array<{
      from_step_order?: number;
      to_step_order: number;
      label?: string;
    }>,
  ) => {
    if (!flowId || !flow) return;

    try {
      setSaving(true);
      await flowService.update(flowId, {
        name: flowName,
        description: flowDescription,
        steps,
        connectors,
      });
      navigate(`/flows/${flowId}`);
    } catch (err) {
      console.error("Error saving flow:", err);
      showToast("Failed to save flow. Please try again.", "error");
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/flows/${flowId}`);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-content-primary">{error || "Flow not found"}</h3>
        <button
          onClick={() => navigate("/flows")}
          className="mt-4 px-4 py-2 bg-surface-secondary text-content-primary rounded-lg hover:bg-interactive-active transition-colors"
        >
          Back to Flows
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="shrink-0 p-6 border-b border-border-primary">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleCancel}
              className="p-2 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-content-primary">Edit Workflow</h1>
              <button
                onClick={() => setShowDetailsEdit(true)}
                className="text-content-tertiary mt-1 hover:text-content-primary text-sm flex items-center gap-2"
              >
                {flowName}
                <span className="text-xs text-content-quaternary">(click to edit name/description)</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto">
          {saving ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 text-brand-primary animate-spin mb-4" />
              <p className="text-content-tertiary">Saving changes...</p>
            </div>
          ) : (
            <FlowBuilder
              initialSteps={flow.steps.map((step) => ({
                id: step.id,
                agentUuid: step.agentUuid,
                agentName: step.agentName,
                config: step.config,
                requiresReview: step.requiresReview,
              }))}
              initialConnectors={flow.connectors?.map((c) => ({
                fromStepId: c.fromStepId,
                toStepId: c.toStepId,
              }))}
              onSave={handleSaveFlow}
              onCancel={handleCancel}
            />
          )}
        </div>
      </div>

      {/* Edit Details Modal */}
      {showDetailsEdit && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <h2 className="text-lg font-semibold text-content-primary">Edit Workflow Details</h2>
              <button
                onClick={() => setShowDetailsEdit(false)}
                className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="flow-name" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Flow Name *
                </label>
                <input
                  id="flow-name"
                  type="text"
                  placeholder="e.g., Customer Support Pipeline"
                  value={flowName}
                  onChange={(e) => setFlowName(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="flow-description" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Description
                </label>
                <textarea
                  id="flow-description"
                  rows={3}
                  placeholder="Describe what this flow does..."
                  value={flowDescription}
                  onChange={(e) => setFlowDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
              <button
                onClick={() => setShowDetailsEdit(false)}
                className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
